/**
 * The preview: your camera and microphone, before anyone else can see them.
 *
 * This is the one piece of media code that runs on the landing page, and it is
 * deliberately plain `getUserMedia` with no LiveKit in it. Two reasons, and
 * the second is the real one:
 *
 *   1. `livekit-client` is ~400 KB the landing page has no use for.
 *   2. The tracks acquired here are the tracks the call PUBLISHES. Handing the
 *      live stream over (`lib/livekit-handoff.ts`) means the camera light does
 *      not blink off and on at the moment of joining, and the browser does not
 *      re-prompt. Acquiring twice is the cheap version and it looks it.
 *
 * The partial-failure handling is not defensive padding: a laptop with a
 * microphone and no webcam is common, and `getUserMedia({audio, video})`
 * rejects the WHOLE request with `NotFoundError` in that case. Falling back to
 * audio-only is the difference between "join and be heard" and "this app does
 * not work on my machine".
 */

import * as React from 'react'
import {
  describeMediaProblem,
  hasMediaDevices,
  readDevicePreferences,
  writeDevicePreferences,
  type MediaProblem,
} from './media'
import { abandonPreflight, stashPreflight, type PreflightMedia } from '@/lib/livekit-handoff'

export type PreviewStatus = 'idle' | 'requesting' | 'ready' | 'blocked'

export interface Preview {
  status: PreviewStatus
  /** Recomposed whenever the set of tracks changes, so `srcObject` re-attaches. */
  stream: MediaStream | null
  problem: MediaProblem | null

  /** A camera track exists. Distinct from `cameraOn`, which is the toggle. */
  hasCamera: boolean
  hasMic: boolean
  cameraOn: boolean
  micOn: boolean

  cameraDeviceId: string | null
  micDeviceId: string | null
  speakerDeviceId: string | null

  /** Bump this into `useDeviceInventory` to re-read labels after a grant. */
  revision: number

  start: () => void
  retry: () => void
  setCameraOn: (on: boolean) => void
  setMicOn: (on: boolean) => void
  chooseCamera: (deviceId: string) => void
  chooseMic: (deviceId: string) => void
  chooseSpeaker: (deviceId: string) => void

  /**
   * Give the live tracks to the call and stop owning them.
   *
   * After this the hook will NOT stop them on unmount — the call does, when it
   * disconnects or fails to connect. Exactly one owner at a time.
   */
  handOff: () => PreflightMedia | null
  /**
   * Take the tracks back after a join that failed.
   *
   * Without it, a 503 on the way into a call leaves a camera that neither side
   * owns: the preview has stopped stopping it and the call never started.
   */
  reclaim: () => void
  release: () => void
}

/**
 * Capture settings chosen for a call that will be recorded and transcribed.
 *
 * The three audio flags are on because the alternative is a transcript full of
 * the other person's voice echoing back. They are also LiveKit's own defaults;
 * being explicit here means the preview and the published track sound the
 * same, which is what makes the preview meaningful.
 */
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}

/** 720p ideal, never exact: an `exact` resolution is how a webcam refuses. */
const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30 },
}

export function usePreview(autoStart = true): Preview {
  const [status, setStatus] = React.useState<PreviewStatus>('idle')
  const [stream, setStream] = React.useState<MediaStream | null>(null)
  const [problem, setProblem] = React.useState<MediaProblem | null>(null)
  const [cameraOn, setCameraOnState] = React.useState(true)
  const [micOn, setMicOnState] = React.useState(true)
  const [revision, setRevision] = React.useState(0)

  const initial = React.useRef(readDevicePreferences())
  const [cameraDeviceId, setCameraDeviceId] = React.useState<string | null>(initial.current.camera)
  const [micDeviceId, setMicDeviceId] = React.useState<string | null>(initial.current.microphone)
  const [speakerDeviceId, setSpeakerDeviceId] = React.useState<string | null>(
    initial.current.speaker,
  )

  // Tracks are held in refs, not state: they are mutable objects with their own
  // lifecycle, and re-rendering is not what stopping a camera should depend on.
  const videoTrack = React.useRef<MediaStreamTrack | null>(null)
  const audioTrack = React.useRef<MediaStreamTrack | null>(null)
  const owned = React.useRef(true)
  // Guards against two overlapping acquisitions — a fast double click on a
  // device picker, or StrictMode's double effect — leaving one camera open
  // with nothing pointing at it.
  const generation = React.useRef(0)

  const recompose = React.useCallback(() => {
    const tracks = [audioTrack.current, videoTrack.current].filter(
      (track): track is MediaStreamTrack => track !== null,
    )
    setStream(tracks.length > 0 ? new MediaStream(tracks) : null)
  }, [])

  const stopOwned = React.useCallback(() => {
    if (!owned.current) return
    videoTrack.current?.stop()
    audioTrack.current?.stop()
  }, [])

  /* ---------------------------------------------------------------------- */

  const acquire = React.useCallback(async () => {
    if (!hasMediaDevices()) {
      setProblem(describeMediaProblem(new Error('no mediaDevices')))
      setStatus('blocked')
      return
    }

    const mine = ++generation.current
    setStatus('requesting')
    setProblem(null)

    const video: MediaTrackConstraints = { ...VIDEO_CONSTRAINTS }
    if (cameraDeviceId) video.deviceId = { ideal: cameraDeviceId }
    const audio: MediaTrackConstraints = { ...AUDIO_CONSTRAINTS }
    if (micDeviceId) audio.deviceId = { ideal: micDeviceId }

    // One call for both, so there is ONE permission prompt rather than two
    // stacked dialogs — the second of which people reflexively dismiss.
    let media: MediaStream | null = null
    let firstFailure: unknown = null
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio, video })
    } catch (error) {
      firstFailure = error
    }

    // The combined request failed. Before believing it, find out whether ONE of
    // the two would have worked: a missing webcam must not cost you your voice.
    if (!media) {
      const problemNow = describeMediaProblem(firstFailure)
      if (problemNow.kind !== 'denied' && problemNow.kind !== 'insecure') {
        media = await tryOne({ audio }).catch(() => null)
        if (!media) media = await tryOne({ video }).catch(() => null)
      }
      if (!media) {
        if (mine !== generation.current) return
        setProblem(problemNow)
        setStatus('blocked')
        return
      }
      // Partial success is still worth explaining — the person should know why
      // there is no picture before they walk into a call.
      if (mine === generation.current) setProblem(problemNow)
    }

    if (mine !== generation.current) {
      for (const track of media.getTracks()) track.stop()
      return
    }

    videoTrack.current?.stop()
    audioTrack.current?.stop()
    videoTrack.current = media.getVideoTracks()[0] ?? null
    audioTrack.current = media.getAudioTracks()[0] ?? null
    owned.current = true

    // The toggles are the source of truth for enablement; the freshly acquired
    // tracks adopt them rather than the other way round, or turning the camera
    // off and then switching device silently turns it back on.
    if (videoTrack.current) videoTrack.current.enabled = cameraOn
    if (audioTrack.current) audioTrack.current.enabled = micOn

    // Only now do device labels exist. The pickers are blank until this bump.
    setRevision((value) => value + 1)
    recompose()
    setStatus('ready')
  }, [cameraDeviceId, cameraOn, micDeviceId, micOn, recompose])

  /* ---------------------------------------------------------------------- */

  /**
   * Swap one device without disturbing the other.
   *
   * `exact` here, unlike the initial acquisition: this is an explicit choice,
   * and silently landing on a different camera than the one just picked would
   * make the picker a lie. An `OverconstrainedError` is the honest outcome.
   */
  const swap = React.useCallback(
    async (kind: 'video' | 'audio', deviceId: string) => {
      const mine = ++generation.current
      const constraints: MediaStreamConstraints =
        kind === 'video'
          ? { video: { ...VIDEO_CONSTRAINTS, deviceId: { exact: deviceId } } }
          : { audio: { ...AUDIO_CONSTRAINTS, deviceId: { exact: deviceId } } }

      let media: MediaStream
      try {
        media = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (error) {
        if (mine === generation.current) setProblem(describeMediaProblem(error))
        return
      }

      if (mine !== generation.current) {
        for (const track of media.getTracks()) track.stop()
        return
      }

      if (kind === 'video') {
        videoTrack.current?.stop()
        videoTrack.current = media.getVideoTracks()[0] ?? null
        if (videoTrack.current) videoTrack.current.enabled = cameraOn
      } else {
        audioTrack.current?.stop()
        audioTrack.current = media.getAudioTracks()[0] ?? null
        if (audioTrack.current) audioTrack.current.enabled = micOn
      }
      setProblem(null)
      recompose()
    },
    [cameraOn, micOn, recompose],
  )

  /* ---------------------------------------------------------------------- */

  const start = React.useCallback(() => {
    if (status === 'requesting' || status === 'ready') return
    void acquire()
  }, [acquire, status])

  const retry = React.useCallback(() => {
    void acquire()
  }, [acquire])

  React.useEffect(() => {
    if (!autoStart) return
    void acquire()
    // Intentionally once. `acquire` closes over the device ids, and re-running
    // it on every toggle would re-open the camera on each keystroke; explicit
    // device changes go through `swap` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    return () => {
      generation.current += 1
      stopOwned()
    }
  }, [stopOwned])

  /* ---------------------------------------------------------------------- */

  const setCameraOn = React.useCallback((on: boolean) => {
    setCameraOnState(on)
    if (videoTrack.current) videoTrack.current.enabled = on
  }, [])

  const setMicOn = React.useCallback((on: boolean) => {
    setMicOnState(on)
    if (audioTrack.current) audioTrack.current.enabled = on
  }, [])

  const chooseCamera = React.useCallback(
    (deviceId: string) => {
      setCameraDeviceId(deviceId)
      writeDevicePreferences({
        camera: deviceId,
        microphone: micDeviceId,
        speaker: speakerDeviceId,
      })
      void swap('video', deviceId)
    },
    [micDeviceId, speakerDeviceId, swap],
  )

  const chooseMic = React.useCallback(
    (deviceId: string) => {
      setMicDeviceId(deviceId)
      writeDevicePreferences({
        camera: cameraDeviceId,
        microphone: deviceId,
        speaker: speakerDeviceId,
      })
      void swap('audio', deviceId)
    },
    [cameraDeviceId, speakerDeviceId, swap],
  )

  const chooseSpeaker = React.useCallback(
    (deviceId: string) => {
      setSpeakerDeviceId(deviceId)
      writeDevicePreferences({
        camera: cameraDeviceId,
        microphone: micDeviceId,
        speaker: deviceId,
      })
    },
    [cameraDeviceId, micDeviceId],
  )

  const handOff = React.useCallback((): PreflightMedia | null => {
    if (!stream) return null
    owned.current = false
    const media: PreflightMedia = {
      stream,
      cameraEnabled: cameraOn && videoTrack.current !== null,
      micEnabled: micOn && audioTrack.current !== null,
      cameraDeviceId,
      micDeviceId,
    }
    stashPreflight(media)
    return media
  }, [cameraDeviceId, cameraOn, micDeviceId, micOn, stream])

  const reclaim = React.useCallback(() => {
    owned.current = true
    abandonPreflight()
  }, [])

  const release = React.useCallback(() => {
    generation.current += 1
    stopOwned()
    videoTrack.current = null
    audioTrack.current = null
    setStream(null)
    setStatus('idle')
  }, [stopOwned])

  return {
    status,
    stream,
    problem,
    // Derived from the composed stream rather than from the refs, so it is a
    // value React has actually re-rendered for. Reading a ref here would be
    // correct only by accident of the two changing together.
    hasCamera: (stream?.getVideoTracks().length ?? 0) > 0,
    hasMic: (stream?.getAudioTracks().length ?? 0) > 0,
    cameraOn,
    micOn,
    cameraDeviceId,
    micDeviceId,
    speakerDeviceId,
    revision,
    start,
    retry,
    setCameraOn,
    setMicOn,
    chooseCamera,
    chooseMic,
    chooseSpeaker,
    handOff,
    reclaim,
    release,
  }
}

function tryOne(constraints: MediaStreamConstraints): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia(constraints)
}
